import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  TablePagination,
  Card,
  CardMedia,
} from '@mui/material';
import { Check, Close, PhotoCamera, Visibility } from '@mui/icons-material';
import { getAssetUrl, pendingStudentAPI, photoUploadAPI } from '../../services/api';

const PendingApprovals = () => {
  const [pendingStudents, setPendingStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminComments, setAdminComments] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState('PENDING');
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [feesData, setFeesData] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const photoInputRef = useRef(null);

  useEffect(() => {
    loadPendingStudents();
  }, [filterStatus]);

  const loadPendingStudents = async () => {
    try {
      setLoading(true);
      setError('');
      const params = filterStatus ? { status: filterStatus } : {};
      const response = await pendingStudentAPI.getAll(params);
      setPendingStudents(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load pending students');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setAdminComments(request.admin_comments || '');
    setIsEditing(false);
    setEditedData({
      student_name: request.student_name,
      standard: request.standard,
      section: request.section,
      phone_number: request.phone_number || '',
    });
    setFeesData({
      fees_amount: request.fees_amount || '',
      fee_type: request.fee_type || 'Manual Fees',
    });
    setPhotoFile(null);
    setPhotoPreview(request.photo_path ? getAssetUrl(request.photo_path) : '');
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
    setSelectedRequest(null);
    setAdminComments('');
    setIsEditing(false);
    setEditedData({});
    setFeesData({});
    setPhotoFile(null);
    setPhotoPreview('');
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const handlePhotoFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please choose a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      setPhotoPreview(readerEvent.target.result);
      setPhotoFile(file);
    };
    reader.readAsDataURL(file);
  };

  const handleApproveRequest = async () => {
    if (!selectedRequest) return;

    try {
      setActionInProgress(true);
      let photoPath = selectedRequest.photo_path || null;

      if (photoFile) {
        const uploadResponse = await photoUploadAPI.upload(photoFile);
        photoPath = uploadResponse.data.photo_path;
      }

      const approvalData = {
        action: 'APPROVED',
        admin_comments: adminComments,
        photo_path: photoPath,
        ...editedData,
        ...feesData,
      };

      await pendingStudentAPI.approve(selectedRequest.id, approvalData);
      setSuccess('Student request approved and added to student list');
      handleCloseDetails();
      loadPendingStudents();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve request');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest) return;

    try {
      setActionInProgress(true);
      await pendingStudentAPI.reject(selectedRequest.id, {
        action: 'REJECTED',
        admin_comments: adminComments,
      });
      setSuccess('Student request rejected');
      handleCloseDetails();
      loadPendingStudents();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reject request');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const displayedStudents = pendingStudents.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Pending Student Approvals
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" fontWeight="bold">
            Filter by Status:
          </Typography>
          <Button
            variant={filterStatus === 'PENDING' ? 'contained' : 'outlined'}
            onClick={() => setFilterStatus('PENDING')}
            color={filterStatus === 'PENDING' ? 'warning' : 'inherit'}
          >
            Pending
          </Button>
          <Button
            variant={filterStatus === 'APPROVED' ? 'contained' : 'outlined'}
            onClick={() => setFilterStatus('APPROVED')}
            color={filterStatus === 'APPROVED' ? 'success' : 'inherit'}
          >
            Approved
          </Button>
          <Button
            variant={filterStatus === 'REJECTED' ? 'contained' : 'outlined'}
            onClick={() => setFilterStatus('REJECTED')}
            color={filterStatus === 'REJECTED' ? 'error' : 'inherit'}
          >
            Rejected
          </Button>
          <Button
            variant={filterStatus === '' ? 'contained' : 'outlined'}
            onClick={() => setFilterStatus('')}
          >
            All
          </Button>
        </Stack>
      </Paper>

      {loading ? (
        <Paper sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Paper>
      ) : pendingStudents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {filterStatus === 'PENDING'
              ? 'No pending student requests at this time.'
              : `No ${filterStatus.toLowerCase()} student requests.`}
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell fontWeight="bold">Student Name</TableCell>
                  <TableCell>Standard</TableCell>
                  <TableCell>Section</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Fees Amount</TableCell>
                  <TableCell>Submitted By</TableCell>
                  <TableCell>Teacher</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted On</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedStudents.map((request) => (
                  <TableRow key={request.id} hover>
                    <TableCell fontWeight="500">{request.student_name}</TableCell>
                    <TableCell>{request.standard}</TableCell>
                    <TableCell>{request.section}</TableCell>
                    <TableCell>{request.phone_number || '-'}</TableCell>
                    <TableCell>{request.fees_amount ? `₹${parseFloat(request.fees_amount).toFixed(2)}` : '-'}</TableCell>
                    <TableCell>{request.submitted_by_username}</TableCell>
                    <TableCell>{request.teacher_name || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={request.status}
                        color={getStatusColor(request.status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{new Date(request.created_at).toLocaleDateString()}</TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleViewDetails(request)}
                        variant="outlined"
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pendingStudents.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* Request Details Dialog */}
      <Dialog open={openDetails} onClose={handleCloseDetails} maxWidth="sm" fullWidth>
        <DialogTitle>Review Student Request</DialogTitle>
        {selectedRequest && (
          <DialogContent sx={{ pt: 2, maxHeight: '70vh', overflow: 'auto' }}>
            <Box sx={{ mb: 2 }}>
              {/* Student Basic Info Section */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" fontWeight="bold">Student Information</Typography>
                  <Button
                    size="small"
                    onClick={() => setIsEditing(!isEditing)}
                    variant={isEditing ? 'contained' : 'outlined'}
                  >
                    {isEditing ? 'Done Editing' : 'Edit Details'}
                  </Button>
                </Box>

                {isEditing ? (
                  <Box>
                    <TextField
                      fullWidth
                      label="Student Name"
                      value={editedData.student_name}
                      onChange={(e) => setEditedData({ ...editedData, student_name: e.target.value })}
                      margin="normal"
                      size="small"
                    />
                    <TextField
                      fullWidth
                      label="Standard/Class"
                      value={editedData.standard}
                      onChange={(e) => setEditedData({ ...editedData, standard: e.target.value })}
                      margin="normal"
                      size="small"
                    />
                    <TextField
                      fullWidth
                      label="Section"
                      value={editedData.section}
                      onChange={(e) => setEditedData({ ...editedData, section: e.target.value })}
                      margin="normal"
                      size="small"
                    />
                    <TextField
                      fullWidth
                      label="Mobile Number"
                      value={editedData.phone_number}
                      onChange={(e) => setEditedData({ ...editedData, phone_number: e.target.value })}
                      margin="normal"
                      size="small"
                    />
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2"><strong>Name:</strong> {editedData.student_name}</Typography>
                    <Typography variant="body2"><strong>Standard:</strong> {editedData.standard}</Typography>
                    <Typography variant="body2"><strong>Section:</strong> {editedData.section}</Typography>
                    <Typography variant="body2"><strong>Phone:</strong> {editedData.phone_number || '-'}</Typography>
                  </Box>
                )}
              </Box>

              {/* Photo Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Student Photo
                </Typography>
                {photoPreview ? (
                  <Card sx={{ maxWidth: 250, mb: 1.5 }}>
                    <CardMedia
                      component="img"
                      image={photoPreview}
                      alt="Student photo"
                      sx={{ height: 250, objectFit: 'cover' }}
                    />
                  </Card>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    No photo uploaded
                  </Typography>
                )}
                {selectedRequest.status === 'PENDING' && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PhotoCamera />}
                      onClick={() => photoInputRef.current?.click()}
                    >
                      {photoPreview ? 'Change Photo' : 'Upload Photo'}
                    </Button>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handlePhotoFileChange}
                    />
                  </>
                )}
              </Box>
              {/* Fees Section */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f0f8ff', borderRadius: 1, border: '1px solid #ddd' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  📋 Fees Information (Assignment Only)
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, p: 1.5, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107' }}>
                  ⚠️ <strong>IMPORTANT:</strong> This section sets the ACTUAL FEES for the student. Payment is NOT made here. 
                  Use the <strong>Fee Payments</strong> page for collecting/recording student payments.
                </Typography>
                <TextField
                  fullWidth
                  label="Fees Amount (₹)"
                  type="number"
                  value={feesData.fees_amount}
                  onChange={(e) => setFeesData({ ...feesData, fees_amount: e.target.value })}
                  margin="normal"
                  size="small"
                  placeholder="Enter fees amount"
                  inputProps={{ step: "0.01", min: "0" }}
                />
                <TextField
                  fullWidth
                  label="Fee Type/Description"
                  value={feesData.fee_type}
                  onChange={(e) => setFeesData({ ...feesData, fee_type: e.target.value })}
                  margin="normal"
                  size="small"
                  placeholder="e.g., Manual Fees, Class Fees"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  ✓ Fee will be recorded and tracked in the Fee Payments system
                </Typography>
              </Box>

              {/* Submission Details */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Submitted By
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {selectedRequest.teacher_name} ({selectedRequest.submitted_by_username})
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Current Status
                </Typography>
                <Chip
                  label={selectedRequest.status}
                  color={getStatusColor(selectedRequest.status)}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Submitted On
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {new Date(selectedRequest.created_at).toLocaleString()}
                </Typography>
              </Box>

              {/* Admin Comments */}
              {selectedRequest.admin_comments && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Previous Admin Comments
                  </Typography>
                  <Typography variant="body2" sx={{ p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    {selectedRequest.admin_comments}
                  </Typography>
                </Box>
              )}

              {selectedRequest.status === 'PENDING' && (
                <>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Admin Comments (Optional)"
                    value={adminComments}
                    onChange={(e) => setAdminComments(e.target.value)}
                    placeholder="Add comments for approval/rejection..."
                    margin="normal"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Comments will be visible to the teacher
                  </Typography>
                </>
              )}
            </Box>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
          {selectedRequest && selectedRequest.status === 'PENDING' && (
            <>
              <Button
                onClick={handleRejectRequest}
                variant="outlined"
                color="error"
                startIcon={<Close />}
                disabled={actionInProgress}
              >
                Reject
              </Button>
              <Button
                onClick={handleApproveRequest}
                variant="contained"
                color="success"
                startIcon={<Check />}
                disabled={actionInProgress}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingApprovals;
